// File ▸ Export For Web, the web editor's half (PLAN-compiled-export.md).
//
// The engine cannot write a file on polygonjs.com: every download funnels through saveBuffer, which
// is blocked in demo mode, and the web build is always in demo mode. And the wasm the export ships
// is COMPILED FOR THE SCENE by a build worker, which is not something a browser can do at all. So
// the press goes to the server.
//
// THE BUILD IS WATCHED IN A TAB OF ITS OWN, not in a panel over the editor. A build takes about
// thirty seconds and ends in a preview of the exported app, and both of those want room: a panel
// covering the node graph is in the way for the whole wait, and the preview inside it is a postage
// stamp. So this file does one thing, presses the button, and `/scenes/<uuid>/exports/latest` is the
// page that shows the build. The editor keeps the screen.
//
// THE PATH COMES FROM THE SERVER, as `page_url` on the answer. The tab lands on the SCENE's build
// page rather than the row's own address, because that is the URL still worth having tomorrow: press
// export again and it shows the new build. This file never assembles a path of its own.
//
// THE TAB IS OPENED BEFORE THE POST, deliberately. A browser allows `window.open` only while the
// user's click still counts as activation, and the engine delivers this action a few frames after
// the click (the scene is serialized first). Opening straight away spends that activation while it
// is still there, and the tab is pointed at the build page once the server answers.
//
// Every refusal here is a SENTENCE THE SERVER WROTE. This file never invents one and never maps a
// status code to copy of its own: the entitlement refusal, the exhausted allowance and the
// unbuildable version all arrive in the JSON body, and the person is shown what they were given.
// (Entitlements::REFUSALS, Usage::EXHAUSTED_MESSAGE, Export::FAILURES.)

// One row of the build page's step list, as the server sends it. The STATE IS THE SERVER'S: which
// steps are behind, which is running and which are ahead is Export#step_state, so the order of the
// build is written in one place and the page only paints what it is told.
export interface ExportStep {
	stage: string;
	message: string;
	state: "done" | "current" | "pending";
}

export interface ExportStatus {
	// The build's public name. Never its database id: the server addresses an export by uuid.
	uuid: string;
	status: "queued" | "building" | "ready" | "failed" | "expired";
	stage?: string | null;
	steps?: ExportStep[] | null;
	// The step of the pipeline this build is on, or null when it is on none of them: waiting for a
	// slot, and everything after the build. Null is what makes the page show the sentence.
	current_step?: string | null;
	message?: string | null;
	error?: string | null;
	download_url?: string | null;
	preview_url?: string | null;
}

// The answer to a press, which carries one field the watcher has no use for: where to go. The
// server names the page (ExportsController#serialize) so there is one place the path is spelled.
export interface StartedExport extends ExportStatus {
	page_url: string;
}

// The two sentences this file owns, and the only ones. Both are about the BROWSER's behaviour rather
// than the build's, which is why the server has nothing to say about them: it cannot know that a
// popup was blocked or that a connection dropped before the request left.
const BLOCKED_MESSAGE =
	"Your browser blocked the tab that shows the build. Allow pop-ups for this site, or find the build on the scene's settings page.";
const UNREACHABLE_MESSAGE = "The export could not be started. Check your connection and try again.";

export interface WebAppExportOptions {
	// POST target, from data-export-url. Absent on a scene that is not saved or not this user's.
	exportUrl?: string;
	// What to say to the person when there is no build to watch: an unsaved scene, a refusal, a
	// blocked tab. Defaults to alert(), which is what the editor's other host-side messages use; the
	// tests pass their own.
	notify?: (message: string) => void;
	// How a tab is opened. Exists so a test can watch what was opened without a browser.
	openTab?: (url: string) => WindowLike | null;
}

// The sliver of Window this file uses. A real `window.open` result satisfies it.
export interface WindowLike {
	location: { replace(url: string): void };
	close(): void;
}

export async function startWebAppExport(options: WebAppExportOptions): Promise<void> {
	const notify = options.notify ?? ((message: string) => window.alert(message));

	if (!options.exportUrl) {
		notify("Save this scene to your account before exporting it as a web app.");
		return;
	}

	// NO `noopener` HERE, and that is not an oversight. `window.open` with noopener returns null by
	// specification: the point of the feature is that the opener cannot reach the new window, and
	// this flow needs exactly that handle, to send the tab to the build page once the server has
	// answered with one. Asking for it anyway left a blank tab sitting there on every press.
	//
	// Nothing is given away by the opener link: the tab is opened on `about:blank`, which inherits
	// this page's own origin, and it is immediately navigated to a path of our own. There is no third
	// party anywhere in it.
	const open = options.openTab ?? ((url: string) => window.open(url, "_blank"));
	const tab = open("about:blank");

	let started: Response;
	try {
		started = await fetch(options.exportUrl, {
			method: "POST",
			headers: {Accept: "application/json", "X-CSRF-Token": csrfToken()},
			credentials: "same-origin",
		});
	} catch {
		tab?.close();
		notify(UNREACHABLE_MESSAGE);
		return;
	}

	const body = (await readJson(started)) as (StartedExport & {error?: string}) | null;
	if (!started.ok || body == null) {
		// 403 (no subscription) and 503 (the build worker cannot build this version, or is not
		// configured here): both carry the server's sentence, and both are the person's answer. A
		// blank tab left open over a refusal would be a second thing to explain.
		tab?.close();
		notify(body?.error ?? "The export was refused.");
		return;
	}

	// There is a row, so there is a page that can show it: queued, building, or a cache hit (plan D6)
	// that is already ready and shows the preview without ever having waited.
	if (tab == null) {
		notify(BLOCKED_MESSAGE);
		return;
	}
	tab.location.replace(body.page_url);
}

async function readJson(res: Response): Promise<unknown | null> {
	try {
		return await res.json();
	} catch {
		return null;
	}
}

function csrfToken(): string {
	return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
}
